// src/components/common/Modal.js
//
// Portal rationale — this component renders `position:fixed` chrome, and per
// the CSS spec any ancestor with a non-`none` backdrop-filter / filter /
// transform becomes the CONTAINING BLOCK for fixed descendants. Every POS
// surface that hosts a Modal sits inside one: the station shell's glass
// chrome (AuroraBackground's `glass()` sets backdropFilter), SellSurface's
// cart column, the waiter panel. Un-portaled, the modal sized itself against
// a glass sub-panel instead of the viewport — on an iPhone that put
// PaymentConfirmModal's Cancel / Process Payment footer below the fold with
// no scroll affordance, so a cashier could not see how to confirm a sale.
//
// document.body is the one node with no filter/backdrop-filter/transform
// ancestor. This is the same fix already applied locally four times over —
// CartPanel.js:40-51, OrdersPanelNative.js, TopBarExtras.js:91-104,
// surfaces/waiter/OrderDrawer.js:33-43 — hoisted to the shared component so
// every reused modal (PaymentConfirmModal, AddonSelectionModal,
// ChangeTableModal, EditingProtectionModal, TableOrderModal) gets it at once
// rather than growing a fifth copy.
import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useLanguage } from '../../context/LanguageContext';

const Modal = ({ isOpen, onClose, title, children, footer, size = 'medium' }) => {

    const { t } = useLanguage();

    useEffect(() => {
        if (!isOpen) return;
        const handleEsc = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', handleEsc);
        return () => window.removeEventListener('keydown', handleEsc);
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const sizeClasses = {
        'small': 'max-w-md',
        'medium': 'max-w-lg',
        'large': 'max-w-4xl',
        'extra-large': 'max-w-6xl'
    };

    return createPortal(
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 text-center">
                <div
                    className="fixed inset-0 transition-opacity"
                    aria-hidden="true"
                    onClick={onClose}
                >
                    <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
                </div>

                <div className={`inline-block w-full ${sizeClasses[size]} p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-lg`}>
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-medium leading-6 text-gray-900">{title}</h3>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-500"
                        >
                            <span className="sr-only">{t('close')}</span>
                            <svg
                                className="h-6 w-6"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                aria-hidden="true"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M6 18L18 6M6 6l12 12"
                                />
                            </svg>
                        </button>
                    </div>

                    <div className="mt-2">
                        {children}
                    </div>

                    {footer && (
                        <div className="mt-4 flex justify-end space-x-2">
                            {footer}
                        </div>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
};

export default Modal;